import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import Reveal from "./Reveal";

const DESTINATIONS = [
  {
    code: "id",
    flag: "🇮🇩",
    name: { id: "Batam · Indonesia", en: "Batam · Indonesia", zh: "印尼巴淡岛", ja: "バタム島", ko: "바탐", vi: "Batam", th: "บาตัม" },
    img: "https://images.unsplash.com/photo-1628466512358-72c66b4271a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    code: "sg",
    flag: "🇸🇬",
    name: { id: "Singapura", en: "Singapore", zh: "新加坡", ja: "シンガポール", ko: "싱가포르", vi: "Singapore", th: "สิงคโปร์" },
    img: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    code: "my",
    flag: "🇲🇾",
    name: { id: "Malaysia", en: "Malaysia", zh: "马来西亚", ja: "マレーシア", ko: "말레이시아", vi: "Malaysia", th: "มาเลเซีย" },
    img: "https://images.unsplash.com/photo-1596422846543-75c6fc197f07?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    code: "cn",
    flag: "🇨🇳",
    name: { id: "China", en: "China", zh: "中国", ja: "中国", ko: "중국", vi: "Trung Quốc", th: "จีน" },
    img: "https://images.unsplash.com/photo-1508804185872-d7badad00f7d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    code: "jp",
    flag: "🇯🇵",
    name: { id: "Jepang", en: "Japan", zh: "日本", ja: "日本", ko: "일본", vi: "Nhật Bản", th: "ญี่ปุ่น" },
    img: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    code: "kr",
    flag: "🇰🇷",
    name: { id: "Korea", en: "Korea", zh: "韩国", ja: "韓国", ko: "한국", vi: "Hàn Quốc", th: "เกาหลี" },
    img: "https://images.unsplash.com/photo-1538485399081-7c8970e87156?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    code: "vn",
    flag: "🇻🇳",
    name: { id: "Vietnam", en: "Vietnam", zh: "越南", ja: "ベトナム", ko: "베트남", vi: "Việt Nam", th: "เวียดนาม" },
    img: "https://images.unsplash.com/photo-1528127269322-539801943592?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    code: "th",
    flag: "🇹🇭",
    name: { id: "Thailand", en: "Thailand", zh: "泰国", ja: "タイ", ko: "태국", vi: "Thái Lan", th: "ไทย" },
    img: "https://images.unsplash.com/photo-1528181304800-259b08848526?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
];

export const Destinations = () => {
  const { t, lang } = useLang();
  // double the list to create seamless marquee
  const items = [...DESTINATIONS, ...DESTINATIONS];

  return (
    <section
      id="destinations"
      data-testid="destinations-section"
      className="relative overflow-hidden bg-[#0C2230] py-24 text-white md:py-28"
    >
      {/* subtle radial accents */}
      <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-[#0E9A57]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-32 h-[28rem] w-[28rem] rounded-full bg-[#1E7FC2]/10 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
        <Reveal>
          <div className="grid items-end gap-8 md:grid-cols-12">
            <div className="md:col-span-7">
              <span className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-[#B7892F]">
                <span className="h-px w-8 bg-[#B7892F]" />
                {t.destinations.eyebrow}
              </span>
              <h2 className="mt-5 font-[Fraunces] text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-5xl">
                {t.destinations.title}
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-base leading-relaxed text-white/70 md:text-lg">
                {t.destinations.sub}
              </p>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="relative z-10 mt-14 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
        <motion.div
          className="flex gap-5"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 50, ease: "linear", repeat: Infinity }}
        >
          {items.map((d, idx) => (
            <div
              key={`${d.code}-${idx}`}
              data-testid={`destination-card-${idx}`}
              className="group relative h-[360px] w-[280px] flex-shrink-0 overflow-hidden rounded-3xl"
            >
              <img
                src={d.img}
                alt={d.name[lang] || d.name.en}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0C2230]/95 via-[#0C2230]/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6">
                <span className="text-3xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]">{d.flag}</span>
                <h3 className="mt-2 font-[Fraunces] text-2xl font-medium leading-tight text-white">
                  {d.name[lang] || d.name.en}
                </h3>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Destinations;
